import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import APIServices from "../services/APIServices";
import Loader from "../components/Loader";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import appToast, { TOAST_TYPE } from "../utils/AppToast";
import {
  capitalizeFirstLetter,
  PAGE_LIMIT,
  selectLoader,
} from "../utils/Helpers";
import SidebarModal from "../components/SidebarModal";
import UserForm from "./sidebars/UserForm";
import AddEditCompany from "./sidebars/AddEditCompany";
import { useAuth } from "../contexts/AuthContext";

const CompanyList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [userId, setUserId] = useState("");

  // 👇 State to switch between company list & users list
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const handler = setTimeout(() => {
      fetchCompanyList(currentPage, searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchCompanyList = async (page, searchQuery = "") => {
    setTableLoading(true);
    try {
      const response = await APIServices.get(
        `/company?page=${page}&limit=${PAGE_LIMIT}&search=${searchQuery}`
      );
      if (response) {
        setCurrentPage(response.currentPage);
        setTotalPages(response.totalPages);
        setCompanyList(response.data);
      }
    } catch (err) {
      setError("Failed to fetch companies.");
      console.error("Error fetching companies:", err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const fetchCompanyUsers = async (companyId) => {
    setUsersLoading(true);
    try {
      const response = await APIServices.get(`/team/${companyId}`);
      if (response?.members) {
        setSelectedCompanyId(companyId);
        setCompanyUsers(response.members);
      } else {
        appToast(response.data.message, TOAST_TYPE.ERROR);
      }
    } catch (err) {
      console.error("Error fetching company users:", err);
      appToast("Failed to fetch company users", TOAST_TYPE.ERROR);
    } finally {
      setUsersLoading(false);
    }
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page && page !== currentPage) {
        fetchCompanyList(page);
      }
    },
    [currentPage]
  );

  const handleEditCompany = (orgId, userId) => {
    setOrgId(orgId);
    setUserId(userId);
    setModalOpen(true);
  };

  const closeAndUpdateList = () => {
    fetchCompanyList(currentPage);
    setModalOpen(false);
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <section className="content adminUser eventtype">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              {/* 👇 Conditional rendering */}
              {usersLoading ? (
                <Loader />
              ) : !selectedCompanyId ? (
                <>
                  {/* --- Company List --- */}
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="card-top">
                        <div className="input-group">
                          <input
                            onChange={(e) => setSearchQuery(e.target.value)}
                            type="search"
                            className="form-control"
                            placeholder="Search Name"
                          />
                          <div className="input-group-append">
                            <i className="fa fa-search"></i>
                          </div>
                        </div>

                        {isSuperAdmin && (
                          <div className="btn-group-right">
                            <button
                              onClick={() => {
                                setModalOpen(true);
                              }}
                              type="submit"
                              className="btn btn-blue btn-block"
                            >
                              + Add Company
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered">
                          <thead>
                            <tr>
                              <th colSpan={4} style={{ width: "600px" }}>
                                Company Name
                              </th>
                              <th>Owner</th>
                              <th>Company Users</th>
                              {isSuperAdmin && (
                                <th className="text-center pr-3">Action</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {tableLoading ? (
                              selectLoader(50)
                            ) : companyList.length > 0 ? (
                              companyList.map((company) => (
                                <tr key={company._id}>
                                  <td
                                    colSpan={4}
                                    style={{
                                      // width: "600px",
                                      cursor: "pointer",
                                      // color: "blue",
                                    }}
                                    onClick={() => {
                                      fetchCompanyUsers(company._id);
                                      setSelectedCompanyName(company.name);
                                    }}
                                  >
                                    {company?.name}
                                  </td>
                                  <td>{company?.createdBy?.fullName || "-"}</td>
                                  <td className="text-center">
                                    {company?.userCount}
                                  </td>
                                  {isSuperAdmin && (
                                    <td className="text-center">
                                      <ul className="actions justify-content-center">
                                        <li>
                                          <button
                                            className="btn btn-eye"
                                            onClick={() => {
                                              fetchCompanyUsers(company._id);
                                              setSelectedCompanyName(
                                                company.name
                                              );
                                            }}
                                          >
                                            <i className="fas fa-eye"></i>
                                          </button>
                                        </li>

                                        <li>
                                          <button
                                            className="btn btn-pen"
                                            onClick={() =>
                                              handleEditCompany(
                                                company?._id,
                                                company?.createdBy?._id
                                              )
                                            }
                                          >
                                            <i className="fas fa-pen"></i>
                                          </button>
                                        </li>
                                      </ul>
                                    </td>
                                  )}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="4">No data found</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <ResponsivePagination
                          current={currentPage}
                          total={totalPages}
                          onPageChange={handlePageChange}
                          maxWidth={10}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* --- Users List --- */}
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="card-top">
                        <span className="company-title">
                          {selectedCompanyName}
                        </span>

                        {isSuperAdmin && (
                          <div className="btn-group-right">
                            <button
                              onClick={() => {
                                setSelectedCompanyId(null);
                                setCompanyUsers([]);
                              }}
                              type="submit"
                              className="btn btn-blue btn-block"
                            >
                              ← Back
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-lg-12">
                      {usersLoading ? (
                        <Loader />
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-striped table-bordered">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Status</th>
                                <th>Role</th>
                              </tr>
                            </thead>
                            <tbody>
                              {companyUsers.length > 0 ? (
                                companyUsers.map((user) => (
                                  <tr key={user._id}>
                                    <td>{user.fullName || "-"}</td>
                                    <td>{user.email}</td>
                                    <td>{user.phoneNumber || "-"}</td>
                                    <td>{user.status}</td>
                                    <td>{user.userType?.name || "-"}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="5">No users found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar modal for Add/Edit Company */}
      <SidebarModal
        extraClass={"edit-profile-modal client_modal"}
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          setOrgId("");
          setUserId("");
        }}
        headerTitle={orgId ? "Edit Company" : "Add Company"}
      >
        <AddEditCompany
          closeModal={closeAndUpdateList}
          orgId={orgId}
          userId={userId}
        />
      </SidebarModal>
    </section>
  );
};

export default CompanyList;
